(function () {
  'use strict';

  if (window.WFNotifications) return;

  const STORAGE_KEY = 'wf_store_notifications_v1';
  const MAX_NOTIFICATIONS = 200;
  const RETENTION_DAYS = 7;
  const TOAST_DURATION = 4500;

  const ITEMS_PER_PAGE = 10;
  let currentPage = 1;

  const ICONS = {
    success:
      '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',

    error:
      '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 9V13M12 17H12.01M10.29 3.86L2.82 17A2 2 0 0 0 4.55 20H19.45A2 2 0 0 0 21.18 17L13.71 3.86A2 2 0 0 0 10.29 3.86Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',

    warning:
      '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 8V12M12 16H12.01M21 12A9 9 0 1 1 3 12A9 9 0 0 1 21 12Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',

    info:
      '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 11V16M12 8H12.01M21 12A9 9 0 1 1 3 12A9 9 0 0 1 21 12Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  };

  /* ========================================================
     TRANSLATIONS
     ======================================================== */

  const TRANSLATIONS = {
    en: {
      justNow: 'Just now',
      minAgo: (m) => `${m} min ago`,
      hrAgo: (h) => `${h} hr ago`,
      daysAgo: (d) => `${d} day${d === 1 ? '' : 's'} ago`,
      close: 'Close notification',
      unread: 'Unread',
      unreadCount: (c) => `${c} unread notification${c === 1 ? '' : 's'}`,
      pageOf: (c, t) => `${c} of ${t}`,
      cartAddTitle: 'Added to cart',
      cartAddMsg: (p, q) => `${p} was added to your cart${q > 1 ? ` (Qty: ${q})` : ''}.`,
      cartRemoveTitle: 'Product removed',
      cartRemoveMsg: (p) => `${p} was removed from your cart successfully.`,
      cartUpdateTitle: 'Cart updated',
      cartUpdateMsg: (p, q) => `${p} quantity was updated${Number.isFinite(q) ? ` to ${q}` : ''}.`,
      cartErrorTitle: 'Cart update failed',
      cartErrorMsg: 'The cart could not be updated. Please try again.'
    },
    ar: {
      justNow: 'الآن',
      minAgo: (m) => `منذ ${m} دقيقة`,
      hrAgo: (h) => `منذ ${h} ساعة`,
      daysAgo: (d) => `منذ ${d} يوم`,
      close: 'إغلاق الإشعار',
      unread: 'غير مقروء',
      unreadCount: (c) => `${c} إشعار غير مقروء`,
      pageOf: (c, t) => `${c} من ${t}`,
      cartAddTitle: 'تمت الإضافة إلى السلة',
      cartAddMsg: (p, q) => `تمت إضافة ${p} إلى سلة التسوق الخاصة بك${q > 1 ? ` (الكمية: ${q})` : ''}.`,
      cartRemoveTitle: 'تمت إزالة المنتج',
      cartRemoveMsg: (p) => `تمت إزالة ${p} من سلة التسوق بنجاح.`,
      cartUpdateTitle: 'تم تحديث السلة',
      cartUpdateMsg: (p, q) => `تم تحديث كمية ${p}${Number.isFinite(q) ? ` إلى ${q}` : ''}.`,
      cartErrorTitle: 'فشل تحديث السلة',
      cartErrorMsg: 'تعذر تحديث سلة التسوق. يرجى المحاولة مرة أخرى.'
    }
  };

  function t(key, ...args) {
    const lang = (document.documentElement.lang || '').toLowerCase().startsWith('ar') ? 'ar' : 'en';
    const val = TRANSLATIONS[lang][key] || TRANSLATIONS['en'][key];
    return typeof val === 'function' ? val(...args) : val;
  }

  /* ========================================================
     HELPERS
     ======================================================== */

  function escapeHTML(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function normaliseImage(value) {
    if (!value) return '';

    if (typeof value === 'string') {
      return value;
    }

    return value.url || value.src || '';
  }

  function createId() {
    return `wf-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  /*
   * Makes a stored notification URL follow the language the visitor
   * is currently browsing in.
   *
   *   English page:  "/ar/products/x" -> "/products/x"
   *   Arabic page:   "/products/x"    -> "/ar/products/x"
   *
   * Full URLs (https://...) and protocol-relative URLs are left alone.
   */
  function localizeUrl(url) {
    if (!url || url.charAt(0) !== '/' || url.indexOf('//') === 0) {
      return url;
    }

    const root =
      (window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root) ||
      '/';

    // "/ar" on Arabic, "" on the default language
    const prefix = root.replace(/\/$/, '');

    // Remove any stored /ar prefix, then apply the current one
    const clean = url.replace(/^\/ar(?=\/|\?|#|$)/, '') || '/';

    return prefix + clean;
  }

  /* ========================================================
     NOTIFICATION BODY SCROLL LOCK
     ======================================================== */

  function setNotificationBodyLock(locked) {
    if (!document.body) return;

    document.body.classList.toggle('wf-notification-open', Boolean(locked));
  }

  function closeAllNotificationPanels() {
    document.querySelectorAll('[data-wf-notification-panel]').forEach((panel) => {
      panel.hidden = true;
    });

    document.querySelectorAll('[data-wf-notification-button]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });

    setNotificationBodyLock(false);
  }

  /* ========================================================
     STORAGE
     ======================================================== */

  function pruneExpired(items) {
    const cutoff = Date.now() - RETENTION_DAYS * 24 * 60 * 60 * 1000;

    return items.filter((item) => {
      const created = new Date(item.createdAt).getTime();

      if (Number.isNaN(created)) {
        return true;
      }

      return created >= cutoff;
    });
  }

  function readNotifications() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      const items = Array.isArray(stored) ? stored : [];

      return pruneExpired(items);
    } catch (error) {
      return [];
    }
  }

  function writeNotifications(items) {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(pruneExpired(items).slice(0, MAX_NOTIFICATIONS))
      );
    } catch (error) {
      // localStorage can be unavailable.
    }
  }

  /* ========================================================
     TIME LABEL
     ======================================================== */

  function timeLabel(timestamp) {
    const date = new Date(timestamp);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const diff = Date.now() - date.getTime();

    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;

    if (diff < minute) {
      return t('justNow');
    }

    if (diff < hour) {
      return t('minAgo', Math.max(1, Math.floor(diff / minute)));
    }

    if (diff < day) {
      return t('hrAgo', Math.max(1, Math.floor(diff / hour)));
    }

    if (diff < 7 * day) {
      return t('daysAgo', Math.max(1, Math.floor(diff / day)));
    }

    return new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    }).format(date);
  }

  /* ========================================================
     NOTIFICATION MARKUP
     ======================================================== */

  function notificationMarkup(item, pageMode) {
    const type = ['success', 'error', 'warning', 'info'].includes(item.type)
      ? item.type
      : 'info';

    const image = item.image
      ? `
        <span class="wf-notification-item__image">
          <img
            src="${escapeHTML(item.image)}"
            alt=""
            loading="lazy"
          >
        </span>
      `
      : `
        <span class="wf-notification-item__icon wf-notification-item__icon--${type}">
          ${ICONS[type]}
        </span>
      `;

    const content = `
      ${image}

      <span class="wf-notification-item__content">

        <span class="wf-notification-item__title">
          ${escapeHTML(item.title)}
        </span>

        ${
          item.message
            ? `
              <span class="wf-notification-item__message">
                ${escapeHTML(item.message)}
              </span>
            `
            : ''
        }

        <time
          class="wf-notification-item__time"
          datetime="${escapeHTML(item.createdAt)}"
        >
          ${escapeHTML(timeLabel(item.createdAt))}
        </time>

      </span>

      ${
        item.read
          ? ''
          : `<span class="wf-notification-item__unread" aria-label="${escapeHTML(t('unread'))}"></span>`
      }
    `;

    const classes =
      `wf-notification-item` +
      `${item.read ? '' : ' is-unread'}` +
      `${pageMode ? ' wf-notification-item--page' : ''}`;

    if (item.url) {
      return `
        <a
          class="${classes}"
          href="${escapeHTML(localizeUrl(item.url))}"
          data-wf-notification-id="${escapeHTML(item.id)}"
        >
          ${content}
        </a>
      `;
    }

    return `
      <button
        type="button"
        class="${classes}"
        data-wf-notification-id="${escapeHTML(item.id)}"
      >
        ${content}
      </button>
    `;
  }

  /* ========================================================
     TOAST
     ======================================================== */

  function ensureToastRegion() {
    let region = document.querySelector('[data-wf-toast-region]');

    if (region) {
      return region;
    }

    region = document.createElement('div');

    region.className = 'wf-toast-region';
    region.setAttribute('data-wf-toast-region', '');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'false');

    document.body.appendChild(region);

    return region;
  }

  function showToast(item) {
    if (!document.body) {
      return;
    }

    const region = ensureToastRegion();

    const type = ['success', 'error', 'warning', 'info'].includes(item.type)
      ? item.type
      : 'info';

    const toast = document.createElement('div');

    toast.className = `wf-toast wf-toast--${type}`;

    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');

    toast.innerHTML = `
      <span class="wf-toast__icon">
        ${ICONS[type]}
      </span>

      <span class="wf-toast__content">

        <strong class="wf-toast__title">
          ${escapeHTML(item.title)}
        </strong>

        ${
          item.message
            ? `
              <span class="wf-toast__message">
                ${escapeHTML(item.message)}
              </span>
            `
            : ''
        }

      </span>

      <button
        type="button"
        class="wf-toast__close"
        aria-label="${escapeHTML(t('close'))}"
        data-wf-toast-close
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
          />
        </svg>
      </button>

      <span
        class="wf-toast__progress"
        aria-hidden="true"
      ></span>
    `;

    region.appendChild(toast);

    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    let removeTimer = window.setTimeout(() => dismissToast(toast), TOAST_DURATION);

    toast.addEventListener('mouseenter', () => {
      window.clearTimeout(removeTimer);
    });

    toast.addEventListener('mouseleave', () => {
      removeTimer = window.setTimeout(() => dismissToast(toast), 1500);
    });
  }

  function dismissToast(toast) {
    if (!toast || toast.classList.contains('is-leaving')) {
      return;
    }

    toast.classList.add('is-leaving');

    window.setTimeout(() => toast.remove(), 260);
  }

  /* ========================================================
     RENDER
     ======================================================== */

  function render() {
    const items = readNotifications();

    const unreadCount = items.filter((item) => !item.read).length;

    /* Notification count */
    document.querySelectorAll('[data-wf-notification-count]').forEach((badge) => {
      badge.textContent = unreadCount > 99 ? '99+' : String(unreadCount);

      if (unreadCount === 0) {
        badge.hidden = true;
        badge.setAttribute('hidden', '');
        badge.style.display = 'none';
      } else {
        badge.hidden = false;
        badge.removeAttribute('hidden');
        badge.style.display = 'inline-flex';
      }

      badge.setAttribute('aria-label', t('unreadCount', unreadCount));
    });

    /* Header dropdown */
    document.querySelectorAll('[data-wf-notification-list]').forEach((list) => {
      const recentItems = items.slice(0, 8);

      list.innerHTML = recentItems.map((item) => notificationMarkup(item, false)).join('');
    });

    /* Dropdown empty state */
    document.querySelectorAll('[data-wf-notification-empty]').forEach((empty) => {
      empty.hidden = items.length !== 0;
    });

    /* Mark all */
    document.querySelectorAll('[data-wf-notification-mark-all]').forEach((button) => {
      button.disabled = unreadCount === 0;
    });

    /* Clear all */
    document.querySelectorAll('[data-wf-notification-clear]').forEach((button) => {
      button.disabled = items.length === 0;
    });

    /* ======================================================
       PAGINATION
       ====================================================== */

    const totalPages = Math.ceil(items.length / ITEMS_PER_PAGE);

    if (totalPages === 0) {
      currentPage = 1;
    } else if (currentPage > totalPages) {
      currentPage = totalPages;
    }

    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;

    const paginatedItems = items.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    document.querySelectorAll('[data-wf-notifications-page-list]').forEach((list) => {
      list.innerHTML = paginatedItems.map((item) => notificationMarkup(item, true)).join('');
    });

    document.querySelectorAll('[data-wf-notifications-page-empty]').forEach((empty) => {
      empty.hidden = items.length !== 0;
    });

    document.querySelectorAll('[data-wf-notification-pagination]').forEach((container) => {
      if (items.length <= ITEMS_PER_PAGE) {
        container.hidden = true;
        return;
      }

      container.hidden = false;

      const prevBtn = container.querySelector('[data-wf-pagination-prev]');
      const nextBtn = container.querySelector('[data-wf-pagination-next]');
      const info = container.querySelector('[data-wf-pagination-info]');

      if (prevBtn) {
        prevBtn.disabled = currentPage === 1;
      }

      if (nextBtn) {
        nextBtn.disabled = currentPage === totalPages || totalPages === 0;
      }

      if (info) {
        info.textContent = t('pageOf', currentPage, totalPages);
      }
    });
  }

  /* ========================================================
     ADD NOTIFICATION
     ======================================================== */

  function add(payload, options = {}) {
    if (!payload || !payload.title) {
      return null;
    }

    const item = {
      id: payload.id || createId(),
      type: payload.type || 'info',
      action: payload.action || 'general',
      title: String(payload.title),
      message: payload.message ? String(payload.message) : '',
      productTitle: payload.productTitle ? String(payload.productTitle) : '',
      image: normaliseImage(payload.image),
      url: payload.url ? String(payload.url) : '',
      createdAt: payload.createdAt || new Date().toISOString(),
      read: Boolean(payload.read)
    };

    const items = readNotifications();

    items.unshift(item);

    writeNotifications(items);

    render();

    if (options.toast !== false && payload.toast !== false) {
      showToast(item);
    }

    document.dispatchEvent(
      new CustomEvent('wf:notifications:changed', {
        detail: {
          item,
          items
        }
      })
    );

    return item;
  }

  /* ========================================================
     READ / CLEAR
     ======================================================== */

  function markRead(id) {
    if (!id) return;

    const items = readNotifications();

    const item = items.find((entry) => entry.id === id);

    if (!item || item.read) {
      return;
    }

    item.read = true;

    writeNotifications(items);

    render();
  }

  function markAllRead() {
    const items = readNotifications().map((item) => ({
      ...item,
      read: true
    }));

    writeNotifications(items);

    render();
  }

  function clear() {
    writeNotifications([]);

    currentPage = 1;

    render();
  }

  /* ========================================================
     CART ACTION
     ======================================================== */

  function cartAction(data) {
    const action = data?.action || 'update';

    const productTitle = data?.productTitle || 'Product';

    const quantity = Number.parseInt(data?.quantity, 10);

    const cartUrl =
      data?.cartUrl ||
      (window.routes && window.routes.cart_url) ||
      '/cart';

    if (action === 'add') {
      return add({
        type: 'success',
        action: 'cart_add',
        title: t('cartAddTitle'),
        message: t('cartAddMsg', productTitle, quantity),
        productTitle,
        image: data?.image,
        url: data?.url || cartUrl
      });
    }

    if (action === 'remove') {
      return add({
        type: 'success',
        action: 'cart_remove',
        title: t('cartRemoveTitle'),
        message: t('cartRemoveMsg', productTitle),
        productTitle,
        image: data?.image,
        url: cartUrl
      });
    }

    return add({
      type: 'success',
      action: 'cart_update',
      title: t('cartUpdateTitle'),
      message: t('cartUpdateMsg', productTitle, quantity),
      productTitle,
      image: data?.image,
      url: cartUrl
    });
  }

  /* ========================================================
     PUBLIC API
     ======================================================== */

  window.WFNotifications = {
    add,

    cartAction,

    markRead,

    markAllRead,

    clear,

    getAll: readNotifications,

    render,

    success(title, message, extra = {}) {
      return add({
        ...extra,
        type: 'success',
        title,
        message
      });
    },

    error(title, message, extra = {}) {
      return add({
        ...extra,
        type: 'error',
        title,
        message
      });
    },

    warning(title, message, extra = {}) {
      return add({
        ...extra,
        type: 'warning',
        title,
        message
      });
    },

    info(title, message, extra = {}) {
      return add({
        ...extra,
        type: 'info',
        title,
        message
      });
    }
  };

  /* ========================================================
     CUSTOM NOTIFICATION EVENT
     ======================================================== */

  document.addEventListener('wf:notification', (event) => {
    if (event.detail) {
      add(event.detail);
    }
  });

  /* ========================================================
     CLICK EVENTS
     ======================================================== */

  document.addEventListener('click', (event) => {
    const bellButton = event.target.closest('[data-wf-notification-button]');
    const closeButton = event.target.closest('[data-wf-notification-close]');
    const markAllButton = event.target.closest('[data-wf-notification-mark-all]');
    const clearButton = event.target.closest('[data-wf-notification-clear]');
    const notificationItem = event.target.closest('[data-wf-notification-id]');
    const toastClose = event.target.closest('[data-wf-toast-close]');
    const prevButton = event.target.closest('[data-wf-pagination-prev]');
    const nextButton = event.target.closest('[data-wf-pagination-next]');

    /* ====================================================
       PAGINATION
       ==================================================== */

    if (prevButton && !prevButton.disabled) {
      event.preventDefault();

      currentPage--;

      render();

      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });

      return;
    }

    if (nextButton && !nextButton.disabled) {
      event.preventDefault();

      currentPage++;

      render();

      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });

      return;
    }

    /* ====================================================
       TOAST CLOSE
       ==================================================== */

    if (toastClose) {
      dismissToast(toastClose.closest('.wf-toast'));

      return;
    }

    /* ====================================================
       BELL
       ==================================================== */

    if (bellButton) {
      event.preventDefault();

      const center = bellButton.closest('[data-wf-notification-center]');

      const panel = center && center.querySelector('[data-wf-notification-panel]');

      if (!panel) {
        return;
      }

      const willOpen = panel.hidden;

      /* Close every other panel */
      document.querySelectorAll('[data-wf-notification-panel]').forEach((otherPanel) => {
        otherPanel.hidden = true;
      });

      document.querySelectorAll('[data-wf-notification-button]').forEach((otherButton) => {
        otherButton.setAttribute('aria-expanded', 'false');
      });

      /* Open / close current panel */
      panel.hidden = !willOpen;

      bellButton.setAttribute('aria-expanded', willOpen ? 'true' : 'false');

      /* Lock background scrolling */
      setNotificationBodyLock(willOpen);

      if (willOpen) {
        render();
      }

      return;
    }

    /* ====================================================
       CLOSE BUTTON
       ==================================================== */

    if (closeButton) {
      event.preventDefault();

      closeAllNotificationPanels();

      return;
    }

    /* ====================================================
       MARK ALL READ
       ==================================================== */

    if (markAllButton) {
      event.preventDefault();

      markAllRead();

      return;
    }

    /* ====================================================
       CLEAR ALL
       ==================================================== */

    if (clearButton) {
      event.preventDefault();

      clear();

      return;
    }

    /* ====================================================
       NOTIFICATION ITEM
       ==================================================== */

    if (notificationItem) {
      markRead(notificationItem.getAttribute('data-wf-notification-id'));

      return;
    }

    /* ====================================================
       CLICK OUTSIDE
       ==================================================== */

    if (!event.target.closest('[data-wf-notification-center]')) {
      closeAllNotificationPanels();
    }
  });

  /* ========================================================
     ESCAPE KEY
     ======================================================== */

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') {
      return;
    }

    closeAllNotificationPanels();
  });

  /* ========================================================
     STORAGE EVENT
     ======================================================== */

  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) {
      render();
    }
  });

  /* ========================================================
     DAWN CART EVENTS
     ======================================================== */

  function connectToDawnCartEvents() {
    if (window.__wfNotificationsPubSubConnected) {
      return true;
    }

    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      return false;
    }

    window.__wfNotificationsPubSubConnected = true;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || event.source !== 'product-form') {
        return;
      }

      const item = event.cartData || {};

      const productTitle = item.product_title || item.title || 'Product';

      const variantTitle =
        item.variant_title && item.variant_title !== 'Default Title'
          ? item.variant_title
          : '';

      const displayTitle = variantTitle ? `${productTitle} - ${variantTitle}` : productTitle;

      cartAction({
        action: 'add',
        productTitle: displayTitle,
        quantity: item.quantity || 1,
        image: item.featured_image || item.image,
        url: item.url
      });
    });

    subscribe(PUB_SUB_EVENTS.cartError, (event) => {
      if (!event) {
        return;
      }

      const message = event.errors || event.message || t('cartErrorMsg');

      add({
        type: 'error',
        action: 'cart_error',
        title: t('cartErrorTitle'),
        message: typeof message === 'string' ? message : JSON.stringify(message)
      });
    });

    return true;
  }

  /* ========================================================
     CONNECTION RETRY
     ======================================================== */

  let connectionAttempts = 0;

  const connectionTimer = window.setInterval(() => {
    connectionAttempts++;

    if (connectToDawnCartEvents() || connectionAttempts >= 40) {
      window.clearInterval(connectionTimer);
    }
  }, 250);

  /* ========================================================
     INITIALISE
     ======================================================== */

  function initialise() {
    render();

    connectToDawnCartEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, {
      once: true
    });
  } else {
    initialise();
  }

  /* ========================================================
     SHOPIFY SECTION LOAD
     ======================================================== */

  document.addEventListener('shopify:section:load', render);
})();